/* eslint-disable import/prefer-default-export */

import dayjs from 'dayjs'
import { useMemo } from 'react'

import { usePaginatedReleaseNotesQuery } from '~generated/hooks'
import { ReleaseNotesFilterInput } from '~generated/types'
import useDebounce from '~lib/hooks/useDebounce'

type UsePaginatedReleaseNotesParams = {
  filter: ReleaseNotesFilterInput
}

export function usePaginatedReleaseNotes({ filter }: UsePaginatedReleaseNotesParams) {
  const queryFilter = useMemo(() => {
    const startDueDate = dayjs(new Date(filter.startDueDate))
    const endDueDate = dayjs(new Date(filter.endDueDate))

    return {
      ...filter,
      // Date mapping required for correct caching in apollo client
      // If pass date as date object it will be used as object literal in cache keyArgs
      // For example instead of {"startDueDate" : "Tue, 30 Apr 2024 22:00:00 GMT"} it will be {"startDueDate" : "{}"}
      ...(startDueDate.isValid() && { startDueDate: startDueDate.toString() }),
      ...(endDueDate.isValid() && { endDueDate: endDueDate.toString() }),
    }
  }, [filter])

  const { data, loading, variables, fetchMore, refetch } = usePaginatedReleaseNotesQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      filter: {
        ...queryFilter,
      },
      pagination: {
        page: 1,
        limit: 10,
      },
    },
  })

  const loadMore = useDebounce(
    () => {
      const { page, totalPages } = data?.paginatedReleaseNotes || {}

      if (!loading && page < totalPages) {
        fetchMore({
          variables: {
            filter: {
              ...queryFilter,
            },
            pagination: {
              page: page + 1,
              limit: 10,
            },
          },
        })
      }
    },
    300,
    [queryFilter, data, loading, fetchMore]
  )

  return {
    loading,
    data,
    variables,
    loadMore,
    refetch,
  }
}
